import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Cake } from "lucide-react";
import { requestManageSession } from "@/lib/manageSession";
import { useAuth } from "@/contexts/AuthContext";
import { consumeManagerReturnTo, consumeManagerSessionNotice } from "@/lib/authSession";
import { teamHubUrl } from "@/lib/teamHub";
import { withOpsBase } from "@/lib/opsBase";
import TsSessionGate from "@/components/mobile/TsSessionGate";
import Login from "@/pages/Login";

const PINK = "#EE2677";

/**
 * Manage front door — phone + OTP, the same identity as the Team hub.
 *
 * Manage was the last surface still on the shared username/password. It now
 * uses the staff identity and trades that session for a manager token, so a
 * Manage action is attributable to a person instead of to `admin`.
 *
 * The password form is kept behind ?password=1 rather than deleted. It is the
 * documented rollback if Firebase OTP is unavailable, and removing it before
 * the phone path has proven itself in daily use would leave ops with no way in.
 */
export default function ManageLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [sessionNotice] = useState(() => consumeManagerSessionNotice());
  const [denied, setDenied] = useState("");
  const [busy, setBusy] = useState(false);

  const usePassword = searchParams.get("password") === "1";

  /** TS session established — trade it for a manager session. */
  const handleReady = useCallback(async () => {
    setBusy(true);
    setDenied("");
    try {
      const token = await requestManageSession();
      login(token);
      navigate(consumeManagerReturnTo(), { replace: true });
    } catch (ex) {
      // 403 is the ordinary case: a real staff member with no Manage access.
      setDenied(ex.message || "Could not open Ops. Try again.");
    } finally {
      setBusy(false);
    }
  }, [login, navigate]);

  if (usePassword) return <Login />;

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F2EA] px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl border border-[#EBB3A9]/40 text-center">
          <div
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
            style={{ background: PINK }}
          >
            <Cake size={32} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-[#2D2D2D]">Ops is admin-only</h1>
          <p className="mt-2 text-sm text-[#2D2D2D]/70">{denied}</p>
          <p className="mt-4 text-sm text-[#2D2D2D]/70">
            Your other apps are on the Team hub.
          </p>
          <a
            href={teamHubUrl()}
            className="mt-5 inline-block w-full rounded-xl px-4 py-2.5 font-bold text-white"
            style={{ background: PINK }}
          >
            Go to Team
          </a>
          <button
            type="button"
            onClick={() => setDenied("")}
            className="mt-3 w-full text-sm font-semibold"
            style={{ color: PINK }}
          >
            Use a different number
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {sessionNotice ? (
        <div className="mx-auto max-w-md px-4 pt-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
            {sessionNotice}
          </div>
        </div>
      ) : null}
      {busy ? (
        <div className="mx-auto max-w-md px-4 pt-4">
          <div className="rounded-lg bg-white p-3 text-center text-sm font-semibold text-[#2D2D2D]/70">
            Opening Manage…
          </div>
        </div>
      ) : null}
      <TsSessionGate onReady={handleReady} />
      <div className="pb-8 text-center">
        <a
          href={`${withOpsBase("/login")}?password=1`}
          className="text-xs font-semibold text-[#2D2D2D]/45 underline"
        >
          Sign in with the shared password instead
        </a>
      </div>
    </div>
  );
}
